/**
 * StateManager - Central registry for all XState actors
 * Part of Sprint 0023: Unified State Observability System
 * 
 * This manager:
 * 1. Creates and tracks all character/projectile actors
 * 2. Provides subscription-based debug updates (only when debug panel is visible)
 * 3. Bridges XState actors with existing game systems
 * 4. Integrates with @statelyai/inspect for live visualization
 */

import { Vector3, AbstractMesh } from '@babylonjs/core';
import type { ActorRefFrom } from 'xstate';
import { 
  createCharacterActor, 
  getCharacterDebugSnapshot,
  type CharacterActor,
} from './characterMachine';
import {
  createProjectileActor,
  getProjectileDebugSnapshot,
  type ProjectileActor,
} from './projectileMachine';
import type { CharacterEvent, ProjectileEvent, CharacterDebugSnapshot, ProjectileDebugSnapshot } from './types';
import type { ProjectileSpec } from '../types/combat';
import { logger } from '../core/Logger';
import { genericDynamicObjectMachine } from './assetMachines';

type DynamicObjectActor = ActorRefFrom<typeof genericDynamicObjectMachine>;

export interface DynamicObjectDebugSnapshot {
  objectId: string;
  state: string;
  health: number;
  context: any;
}

// ============================================================================
// Debug Subscription Types
// ============================================================================

export interface StateTransition {
  characterId: string;
  timestamp: number;
  from: string;
  to: string;
  event: string;
}

export interface EventLogEntry {
  characterId: string;
  timestamp: number;
  event: any; // Full event object to preserve all properties
  stateBefore: string;
  stateAfter: string;
  transitioned: boolean;
}

export interface StateDebugSnapshot {
  timestamp: number;
  characters: CharacterDebugSnapshot[];
  projectiles: ProjectileDebugSnapshot[];
  dynamicObjects: DynamicObjectDebugSnapshot[];
  recentTransitions: StateTransition[];
  eventLog: EventLogEntry[];
}

export type StateDebugSubscriber = (snapshot: StateDebugSnapshot) => void;

// ============================================================================
// StateManager Singleton
// ============================================================================

export class StateManager {
  private static instance: StateManager | null = null;
  
  // Actor registries
  private characterActors: Map<string, CharacterActor> = new Map();
  private projectileActors: Map<string, ProjectileActor> = new Map();
  private dynamicObjectActors: Map<string, DynamicObjectActor> = new Map();
  
  // Mesh → ObjectId mapping for combat damage lookups
  private meshToObjectId: Map<AbstractMesh, string> = new Map();
  private objectIdToMesh: Map<string, AbstractMesh> = new Map();
  
  // Debug subscriptions
  private debugSubscribers: Set<StateDebugSubscriber> = new Set();
  private debugUpdateInterval: number | null = null;
  private readonly DEBUG_UPDATE_RATE_MS = 50; // 20 FPS for debug UI
  
  // Transition history (ring buffer)
  private transitionHistory: StateTransition[] = [];
  private readonly MAX_TRANSITION_HISTORY = 20;
  
  // Event log (ring buffer) - tracks ALL events, not just those causing transitions
  private eventLog: EventLogEntry[] = [];
  private readonly MAX_EVENT_LOG = 50;
  
  // Cleanup subscriptions (for actor.subscribe returns)
  private actorSubscriptions: Map<string, () => void> = new Map();
  private dynamicObjectSubscriptions: Map<string, () => void> = new Map();
  
  private constructor() {
    logger.core.log('[StateManager] Initialized');
  }
  
  static getInstance(): StateManager {
    if (!StateManager.instance) {
      StateManager.instance = new StateManager();
    }
    return StateManager.instance;
  }
  
  // ============================================================================
  // Character Actor Management
  // ============================================================================
  
  createCharacter(characterId: string, maxHealth: number): CharacterActor {
    if (this.characterActors.has(characterId)) {
      logger.core.warn(`[StateManager] Character actor ${characterId} already exists`);
      return this.characterActors.get(characterId)!;
    }
    
    // Get inspector callback if available (initialized at app boot)
    // const inspectCallback = getInspectCallback();
    
    const actor = createCharacterActor(characterId, maxHealth);
    actor.start();
    
    this.characterActors.set(characterId, actor);
    logger.core.log(`[StateManager] Created character actor: ${characterId}`);
    
    // Subscribe to state changes for debugging
    if (this.debugSubscribers.size > 0) {
      this.subscribeToActor(characterId, actor);
    }
    
    return actor;
  }
  
  getCharacterActor(characterId: string): CharacterActor | undefined {
    return this.characterActors.get(characterId);
  }
  
  getCharacterActorCount(): number {
    return this.characterActors.size;
  }
  
  /**
   * Send an event to a character's XState actor.
   * @returns true if the event caused a state transition, false otherwise
   */
  sendCharacterEvent(characterId: string, event: CharacterEvent): boolean {
    const actor = this.characterActors.get(characterId);
    if (!actor) {
      logger.core.warn(`[StateManager] Character actor ${characterId} not found`);
      return false;
    }
    
    // Capture state before transition (for history)
    const beforeSnapshot = actor.getSnapshot();
    const beforeState = this.formatStateValue(beforeSnapshot.value);
    
    actor.send(event);
    
    // Capture state after transition
    const afterSnapshot = actor.getSnapshot();
    const afterState = this.formatStateValue(afterSnapshot.value);
    
    // Log ALL events (for debugging)
    const transitioned = beforeState !== afterState;
    this.recordEvent(characterId, event, beforeState, afterState, transitioned);
    
    // Record transition if state changed
    if (transitioned) {
      this.recordTransition(characterId, beforeState, afterState, event.type);
    }
    
    return transitioned;
  }
  
  private formatStateValue(value: unknown): string {
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      // For parallel states, show the most interesting region
      const obj = value as Record<string, unknown>;
      if (obj.combat && obj.combat !== 'ready') {
        return `combat:${this.formatStateValue(obj.combat)}`;
      }
      if (obj.locomotion) {
        return String(obj.locomotion);
      }
      return JSON.stringify(value);
    }
    return String(value);
  }
  
  removeCharacter(characterId: string): void {
    const actor = this.characterActors.get(characterId);
    if (actor) {
      actor.stop();
      this.characterActors.delete(characterId);
      
      const unsub = this.actorSubscriptions.get(characterId);
      if (unsub) {
        unsub();
        this.actorSubscriptions.delete(characterId);
      }
      
      logger.core.log(`[StateManager] Removed character actor: ${characterId}`);
    }
  }
  
  // ============================================================================
  // Projectile Actor Management
  // ============================================================================
  
  createProjectile(
    projectileId: string,
    ownerId: string,
    spec: ProjectileSpec,
    position: Vector3,
    velocity: Vector3
  ): ProjectileActor {
    if (this.projectileActors.has(projectileId)) {
      logger.core.warn(`[StateManager] Projectile actor ${projectileId} already exists`);
      return this.projectileActors.get(projectileId)!;
    }
    
    const actor = createProjectileActor(projectileId, ownerId, spec, position, velocity);
    actor.start();
    
    this.projectileActors.set(projectileId, actor);
    logger.core.debug(`[StateManager] Created projectile actor: ${projectileId}`);
    
    // Auto-cleanup when projectile reaches final state
    const subscription = actor.subscribe((snapshot) => {
      if (snapshot.status === 'done') {
        this.removeProjectile(projectileId);
      }
    });
    this.actorSubscriptions.set(projectileId, subscription.unsubscribe);
    
    return actor;
  }
  
  getProjectileActor(projectileId: string): ProjectileActor | undefined {
    return this.projectileActors.get(projectileId);
  }
  
  sendProjectileEvent(projectileId: string, event: ProjectileEvent): void {
    const actor = this.projectileActors.get(projectileId);
    if (!actor) {
      logger.core.warn(`[StateManager] Projectile actor ${projectileId} not found`);
      return;
    }
    actor.send(event);
  }
  
  removeProjectile(projectileId: string): void {
    const actor = this.projectileActors.get(projectileId);
    if (actor) {
      actor.stop();
      this.projectileActors.delete(projectileId);
      
      const unsub = this.actorSubscriptions.get(projectileId);
      if (unsub) {
        unsub();
        this.actorSubscriptions.delete(projectileId);
      }
      
      logger.core.debug(`[StateManager] Removed projectile actor: ${projectileId}`);
    }
  }

  // ============================================================================
  // Dynamic Object Actor Management
  // ============================================================================

  registerDynamicObject(objectId: string, actor: DynamicObjectActor): void {
    if (this.dynamicObjectActors.has(objectId)) {
      logger.core.error(`[StateManager] ❌ DUPLICATE ID: Dynamic object actor '${objectId}' already exists! This will cause state panel issues.`);
      logger.core.error(`   Existing actor:`, this.dynamicObjectActors.get(objectId));
      logger.core.error(`   New actor:`, actor);
      // Don't return - overwrite to at least show something
    }

    this.dynamicObjectActors.set(objectId, actor);

    // Auto-cleanup when object finishes
    const subscription = actor.subscribe((snapshot) => {
      if (snapshot.status === 'done') {
        this.unregisterDynamicObject(objectId);
      }
    });

    this.dynamicObjectSubscriptions.set(objectId, subscription.unsubscribe);
    logger.core.log(`[StateManager] Registered dynamic object actor: ${objectId}`);
  }

  unregisterDynamicObject(objectId: string): void {
    const actor = this.dynamicObjectActors.get(objectId);
    if (!actor) return;

    const unsub = this.dynamicObjectSubscriptions.get(objectId);
    if (unsub) {
      unsub();
      this.dynamicObjectSubscriptions.delete(objectId);
    }

    actor.stop?.();
    this.dynamicObjectActors.delete(objectId);
    logger.core.debug(`[StateManager] Unregistered dynamic object actor: ${objectId}`);
  }

  getDynamicObjectActor(objectId: string): DynamicObjectActor | undefined {
    return this.dynamicObjectActors.get(objectId);
  }

  getDynamicObjectActorCount(): number {
    return this.dynamicObjectActors.size;
  }

  /**
   * Register a mesh as a damageable object target
   */
  registerDamageableMesh(mesh: AbstractMesh, objectId: string): void {
    this.meshToObjectId.set(mesh, objectId);
    this.objectIdToMesh.set(objectId, mesh);
    logger.core.debug(`[StateManager] Registered damageable mesh: ${mesh.name} → ${objectId}`);
  }

  /**
   * Unregister a damageable mesh
   */
  unregisterDamageableMesh(objectId: string): void {
    const mesh = this.objectIdToMesh.get(objectId);
    if (mesh) {
      this.meshToObjectId.delete(mesh);
    }
    this.objectIdToMesh.delete(objectId);
  }

  /**
   * Get objectId from mesh (for combat hit detection)
   */
  getObjectIdFromMesh(mesh: AbstractMesh): string | undefined {
    return this.meshToObjectId.get(mesh);
  }

  /**
   * Get mesh for an object (for raycast targeting)
   */
  getMeshForObject(objectId: string): AbstractMesh | undefined {
    return this.objectIdToMesh.get(objectId);
  }

  /**
   * Get all damageable meshes (for combat raycasting)
   */
  getAllDamageableMeshes(): AbstractMesh[] {
    return Array.from(this.meshToObjectId.keys());
  }

  /**
   * Apply damage to a dynamic object by ID
   * Note: impactForce and impactDirection are reserved for future physics impulse application
   */
  applyDamageToObject(objectId: string, amount: number, _impactForce: number = 10, _impactDirection?: Vector3): boolean {
    const actor = this.dynamicObjectActors.get(objectId);
    if (!actor) {
      logger.core.warn(`[StateManager] Cannot apply damage: object '${objectId}' not found`);
      return false;
    }

    // Generic machine only accepts amount; physics impulse could be added later
    actor.send({
      type: 'DAMAGE_RECEIVED',
      amount,
    });

    logger.core.log(`[StateManager] 💥 Damage applied to ${objectId}: ${amount} damage`);
    return true;
  }
  
  // ============================================================================
  // Debug Subscription API (for unified debug panel)
  // ============================================================================
  
  /**
   * Subscribe to debug state updates.
   * Starts the debug update loop when first subscriber connects.
   * Returns unsubscribe function.
   */
  subscribeDebug(callback: StateDebugSubscriber): () => void {
    this.debugSubscribers.add(callback);
    
    // Start debug loop if this is first subscriber
    if (this.debugSubscribers.size === 1) {
      this.startDebugLoop();
    }
    
    // Immediately send current state
    callback(this.getDebugSnapshot());
    
    logger.core.debug(`[StateManager] Debug subscriber added (total: ${this.debugSubscribers.size})`);
    
    return () => {
      this.debugSubscribers.delete(callback);
      
      // Stop debug loop if no more subscribers
      if (this.debugSubscribers.size === 0) {
        this.stopDebugLoop();
      }
      
      logger.core.debug(`[StateManager] Debug subscriber removed (total: ${this.debugSubscribers.size})`);
    };
  }
  
  private startDebugLoop(): void {
    if (this.debugUpdateInterval !== null) return;
    
    this.debugUpdateInterval = window.setInterval(() => {
      const snapshot = this.getDebugSnapshot();
      for (const subscriber of this.debugSubscribers) {
        try {
          subscriber(snapshot);
        } catch (error) {
          logger.core.error('[StateManager] Debug subscriber error:', error);
        }
      }
    }, this.DEBUG_UPDATE_RATE_MS);
    
    logger.core.debug('[StateManager] Debug loop started');
  }
  
  private stopDebugLoop(): void {
    if (this.debugUpdateInterval !== null) {
      window.clearInterval(this.debugUpdateInterval);
      this.debugUpdateInterval = null;
      logger.core.debug('[StateManager] Debug loop stopped');
    }
  }
  
  private getDebugSnapshot(): StateDebugSnapshot {
    const characters: CharacterDebugSnapshot[] = [];
    const projectiles: ProjectileDebugSnapshot[] = [];
    const dynamicObjects: DynamicObjectDebugSnapshot[] = [];
    
    for (const actor of this.characterActors.values()) {
      try {
        characters.push(getCharacterDebugSnapshot(actor));
      } catch (error) {
        logger.core.error('[StateManager] Error getting character debug snapshot:', error);
      }
    }
    
    for (const actor of this.projectileActors.values()) {
      try {
        projectiles.push(getProjectileDebugSnapshot(actor));
      } catch (error) {
        logger.core.error('[StateManager] Error getting projectile debug snapshot:', error);
      }
    }

    for (const actor of this.dynamicObjectActors.values()) {
      try {
        const snapshot = actor.getSnapshot();
        dynamicObjects.push({
          objectId: snapshot.context.id || 'unknown',
          state: snapshot.value as string,
          health: snapshot.context.health || 0,
          context: snapshot.context
        });
      } catch (error) {
        logger.core.error('[StateManager] Error getting dynamic object debug snapshot:', error);
      }
    }
    
    return {
      timestamp: performance.now(),
      characters,
      projectiles,
      dynamicObjects,
      recentTransitions: [...this.transitionHistory],
      eventLog: [...this.eventLog],
    };
  }
  
  /**
   * Record a state transition for debug history.
   * Called automatically when events cause state changes.
   */
  recordTransition(characterId: string, from: string, to: string, event: string): void {
    const transition: StateTransition = {
      characterId,
      timestamp: performance.now(),
      from,
      to,
      event,
    };
    
    this.transitionHistory.push(transition);
    
    // Trim to max size (ring buffer)
    if (this.transitionHistory.length > this.MAX_TRANSITION_HISTORY) {
      this.transitionHistory.shift();
    }
  }
  
  /**
   * Record an event in the event log (all events, not just transitions).
   */
  private recordEvent(
    characterId: string,
    event: any,
    stateBefore: string,
    stateAfter: string,
    transitioned: boolean
  ): void {
    // Skip high-frequency events that don't cause transitions
    if (!transitioned && (event.type === 'ANIM_TIME_UPDATE' || event.type === 'PHYSICS_GROUNDED')) {
      return;
    }
    
    const entry: EventLogEntry = {
      characterId,
      timestamp: performance.now(),
      event: event,
      stateBefore,
      stateAfter,
      transitioned,
    };
    
    this.eventLog.push(entry);
    
    // Trim to max size (ring buffer)
    if (this.eventLog.length > this.MAX_EVENT_LOG) {
      this.eventLog.shift();
    }
  }
  
  private subscribeToActor(_actorId: string, _actor: CharacterActor): void {
    // This is called when debug subscribers exist and a new actor is created
    // The debug loop will pick up the new actor automatically
  }
  
  // ============================================================================
  // Utility Methods
  // ============================================================================
  
  getCharacterCount(): number {
    return this.characterActors.size;
  }
  
  getProjectileCount(): number {
    return this.projectileActors.size;
  }
  
  getAllCharacterIds(): string[] {
    return Array.from(this.characterActors.keys());
  }
  
  getAllProjectileIds(): string[] {
    return Array.from(this.projectileActors.keys());
  }
  
  /**
   * Reset all state (for level transitions, etc.)
   */
  reset(): void {
    // Stop all actors
    for (const actor of this.characterActors.values()) {
      actor.stop();
    }
    for (const actor of this.projectileActors.values()) {
      actor.stop();
    }
    
    // Clear all subscriptions
    for (const unsub of this.actorSubscriptions.values()) {
      unsub();
    }
    
    this.characterActors.clear();
    this.projectileActors.clear();
    this.actorSubscriptions.clear();
    
    logger.core.log('[StateManager] Reset complete');
  }
  
  /**
   * Dispose of the StateManager (for cleanup)
   */
  dispose(): void {
    this.stopDebugLoop();
    this.reset();
    this.debugSubscribers.clear();
    StateManager.instance = null;
    logger.core.log('[StateManager] Disposed');
  }
}
